/**
 * Zod schema for the "Edit Agent" form (F031 Agent Registry, form-spec `FS-F031-AGENT`, A-02) —
 * Doc 69 Layer 2 output, imported by `AgentEditForm.tsx`, never redefined
 * (form-standard.context.md §3). Rule IDs are the form-spec's own `V-` IDs. Per C-02, Agent Name
 * (`V-01`/`V-02`) and Owner Id (`V-07`/`V-08`) are NOT on `UpdateAgentRequest` — immutable on edit,
 * rendered read-only — so they have no rule here; see `AgentRegister.schema.ts` for those two.
 *
 * The schema validates the full editable-field snapshot (not the sparse PATCH body) — the sparse
 * `UpdateAgentRequest` diff against the loaded snapshot is built in `AgentEditForm.tsx`'s submit
 * handler, per A-02 ("null members = unchanged"). `metadataJson` is the one field that is NOT
 * diffed: there is no source field to prefill it from (`AgentDetailDto` doesn't expose it), so a
 * blank value always means "leave unchanged" and a non-blank value is always sent, matching the
 * pre-migration behaviour.
 */
import { z } from 'zod';
import type { AgentEnvironment, DataAccessLevel, ToolAccessLevel } from '../../../types';
import { AGENT_ENVIRONMENT_OPTIONS, DATA_ACCESS_LEVEL_OPTIONS, TOOL_ACCESS_LEVEL_OPTIONS } from './agentFormFields';

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

// reason: AGENT_ENVIRONMENT_OPTIONS/DATA_ACCESS_LEVEL_OPTIONS/TOOL_ACCESS_LEVEL_OPTIONS are
// runtime-verified non-empty arrays of the exported union types; zod's z.enum() requires a literal
// non-empty tuple type.
const ENVIRONMENT_OPTIONS = AGENT_ENVIRONMENT_OPTIONS as [AgentEnvironment, ...AgentEnvironment[]];
const DATA_ACCESS_LEVEL_TUPLE = DATA_ACCESS_LEVEL_OPTIONS as [DataAccessLevel, ...DataAccessLevel[]];
const TOOL_ACCESS_LEVEL_TUPLE = TOOL_ACCESS_LEVEL_OPTIONS as [ToolAccessLevel, ...ToolAccessLevel[]];

export const AgentEditSchema = z.object({
  // V-03 (required @ submit) / V-04 (max 50 @ blur)
  agentVersion: z
    .string()
    .trim()
    .min(1, 'Agent version is required')
    .max(50, 'Agent version must be 50 characters or fewer'),
  // V-05 (required @ submit) / V-06 (max 1000 @ blur)
  purpose: z
    .string()
    .trim()
    .min(1, 'Purpose is required')
    .max(1000, 'Purpose must be 1000 characters or fewer'),
  // V-09 (optional, max 200 @ blur)
  ownerTeam: z
    .string()
    .max(200, 'Owner team must be 200 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // V-10
  environment: z.enum(ENVIRONMENT_OPTIONS, { message: 'Select an environment' }),
  // V-11 (required @ submit) / V-12 (max 100 @ blur)
  modelProvider: z
    .string()
    .trim()
    .min(1, 'Model provider is required')
    .max(100, 'Model provider must be 100 characters or fewer'),
  // V-13 (required @ submit) / V-14 (max 200 @ blur)
  modelName: z
    .string()
    .trim()
    .min(1, 'Model name is required')
    .max(200, 'Model name must be 200 characters or fewer'),
  // V-15 (optional, max 100 @ blur)
  runtimeProvider: z
    .string()
    .max(100, 'Runtime provider must be 100 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // V-16
  dataAccessLevel: z.enum(DATA_ACCESS_LEVEL_TUPLE, { message: 'Select a data access level' }),
  // V-17
  toolAccessLevel: z.enum(TOOL_ACCESS_LEVEL_TUPLE, { message: 'Select a tool access level' }),
  mcpEnabled: z.boolean(),
  // V-18 (optional, valid JSON when non-empty @ blur) — blank always means "unchanged" (A-02).
  metadataJson: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || isValidJson(v), { message: 'Metadata must be valid JSON' })
    .transform((v) => v || undefined),
});

/** The shape RHF's `useForm<T>`/`defaultValues`/`reset()` are typed against (pre-transform). */
export type AgentEditFormValues = z.input<typeof AgentEditSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed, undefined-if-empty). */
export type AgentEditFormOutput = z.output<typeof AgentEditSchema>;
